import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { SetAuthAction, ValidateTokenAction } from '@tsin-core/actions/auth.action';
import { AuthState } from '@tsin-core/states/auth.state';
import { ConfigService } from '@tsin-core/services/common/config.service';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="callback-container">
      <div class="callback-content">
        <div class="spinner"></div>
        <h2>Authenticating...</h2>
        <p>{{ message }}</p>
        @if (error) {
          <div class="error-message">
            <p>{{ error }}</p>
            <button (click)="retryLogin()">Retry Login</button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .callback-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
    .callback-content {
      background: white;
      padding: 3rem;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
      text-align: center;
      max-width: 400px;
      width: 90%;
    }
    .spinner {
      border: 4px solid #f3f3f3;
      border-top: 4px solid #667eea;
      border-radius: 50%;
      width: 50px;
      height: 50px;
      animation: spin 1s linear infinite;
      margin: 0 auto 1.5rem;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    h2 { color: #333; margin-bottom: 1rem; font-size: 1.5rem; }
    p { color: #666; margin-bottom: 1rem; }
    .error-message {
      margin-top: 1.5rem;
      padding: 1rem;
      background: #fee;
      border-radius: 8px;
      border-left: 4px solid #f44;
    }
    .error-message p { color: #c33; font-weight: 500; }
    button {
      margin-top: 1rem;
      padding: 0.75rem 1.5rem;
      background: #667eea;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 1rem;
      transition: background 0.3s;
    }
    button:hover { background: #5568d3; }
  `]
})
export class CallbackComponent implements OnInit {
  message = 'Processing authentication...';
  error: string | null = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    private store: Store,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    console.log('Auth Callback Component initialized');
    this.handleCallback();
  }

  private async handleCallback(): Promise<void> {
    // Read the id_token from the shared SSO cookie set by auth-ui
    const idToken = this.getCookie('id_token');

    if (!idToken) {
      console.error('❌ No id_token cookie found');
      this.error = 'No authentication token found. Please log in again.';
      this.message = 'Authentication failed';
      return;
    }

    try {
      console.log('🔑 id_token cookie found, exchanging for WWUS service JWT...');
      this.message = 'Exchanging authentication token...';

      const authServiceUrl = this.configService.getAuthServiceUrl() || 'https://auth.api.stage.tsin.ca';
      const response: any = await this.http.post(
        `${authServiceUrl}/api/auth/cognito/token-exchange`,
        { cognitoIdToken: idToken, service: 'WWUS' }
      ).toPromise();

      const token = response?.data?.token ?? response?.token;
      const user  = response?.data?.user  ?? response?.user;
      const refreshToken = response?.data?.refreshToken ?? response?.refreshToken ?? null;
      const success = response?.success ?? false;

      if (success && token) {
        console.log('✅ Token exchange successful');

        localStorage.setItem('token', token);
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }

        // Extract the WWUS-specific role from the profiles array
        const enrichedUser = this.extractWwusRole(user);
        console.log('Enriched user with WWUS role:', enrichedUser?.role);

        // 1. Set auth state immediately
        this.store.dispatch(new SetAuthAction({ token, refreshToken, user: enrichedUser }));

        // 2. Try to fetch the real user profile from the WWUS backend
        this.message = 'Loading profile...';
        try {
          await this.store.dispatch(new ValidateTokenAction({ token, refreshToken })).toPromise();
          let realUser = this.store.selectSnapshot(AuthState.getCurrentUser);
          if (realUser) {
            // Preserve global superAdmin role — the WWUS backend /auth/me may
            // return a lower role (e.g. 'admin') which would override the
            // superAdmin extracted from the auth-service profiles.
            if (enrichedUser?.role === 'superAdmin' && realUser.role !== 'superAdmin') {
              realUser = { ...realUser, role: 'superAdmin', isStaff: true };
            }
            this.message = 'Authentication successful! Redirecting...';
            this.determineAuthStatus(realUser);
          } else {
            console.warn('⚠️ WWUS /auth/me returned no user, using auth-service data');
            this.determineAuthStatus(enrichedUser);
          }
        } catch (profileError) {
          console.warn('⚠️ WWUS profile fetch failed, navigating with auth-service data:', profileError);
          this.determineAuthStatus(enrichedUser);
        }
      } else {
        throw new Error(response.message || 'Token exchange failed');
      }
    } catch (error: any) {
      console.error('❌ Callback error:', error);
      this.error = error.message || 'An unexpected error occurred';
      this.message = 'Authentication failed';
    }
  }

  /**
   * Extract the WWUS-specific role from the central auth service response.
   * Global superAdmin always takes priority over any service-specific role.
   */
  private extractWwusRole(user: any): any {
    if (!user) return user;

    // Global superAdmin check: if any profile has superAdmin, honour it globally
    if (Array.isArray(user.profiles) && user.profiles.some((p: any) => p.role === 'superAdmin')) {
      return { ...user, role: 'superAdmin', isStaff: true };
    }

    // Check roles array for superAdmin
    if (Array.isArray(user.roles) && user.roles.includes('superAdmin')) {
      return { ...user, role: 'superAdmin', isStaff: true };
    }

    if (Array.isArray(user.profiles) && user.profiles.length > 0) {
      const wwusProfile = user.profiles.find((p: any) =>
        p.project === 'WWUS' || p.project === 'WORK_WITH_US'
      );
      if (wwusProfile?.role) {
        return { ...user, role: wwusProfile.role };
      }
      if (user.profiles[0]?.role) {
        return { ...user, role: user.profiles[0].role };
      }
    }

    if (Array.isArray(user.roles) && user.roles.length > 0) {
      return { ...user, role: user.roles[0] };
    }

    return user;
  }

  private determineAuthStatus(user: any): void {
    if (!user) {
      this.router.navigate(['/employee/profile/overview']);
      return;
    }
    // superAdmin is always treated as staff with full management access
    if (user.role === 'superAdmin') {
      this.router.navigate(['/management']);
      return;
    }
    if (!user.isStaff) {
      this.router.navigate(['/employee/profile/overview']);
      return;
    }
    if (user.role === 'hrAdmin' || user.role === 'facilitatorAdmin') {
      this.router.navigate(['/management']);
    } else {
      this.router.navigate(['/employee']);
    }
  }

  /** Read a cookie value by name. */
  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  retryLogin(): void {
    const authUiUrl = this.configService.getAuthUiUrl();
    if (authUiUrl) {
      const returnTo = window.location.origin + '/auth/callback';
      window.location.href = `${authUiUrl}/login?returnTo=${encodeURIComponent(returnTo)}`;
    } else {
      this.router.navigate(['/account/validate-token']);
    }
  }
}

